import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { getClassesBySchoolId, getLoginBypass, getQR } from "../services/TeacherAPIClient";
import { exportLoginBypassRowsToExcel } from "../services/ExcelExporter";
import { getDistinctClassGradeLabel } from "../utils/classDisplay";
import "./TeacherProvideLoginBypassModal.css";

const reasons = [
  {
    label: "QR code will not scan",
    helper: "Device, camera, or school security setting may be blocking it"
  },
  {
    label: "QR opens the wrong place",
    helper: "The QR link does not reach the screener"
  },
  {
    label: "Search for an individual child instead",
    helper: "Use this only when one child needs a login link"
  },
  {
    label: "Other",
    helper: "Add a short note"
  }
];
const INDIVIDUAL_REASON = "Search for an individual child instead";

const getClassId = (classItem) => classItem?.id || classItem?._id || classItem?.classid || classItem?.classId;
const getStudentId = (student) => student?.id || student?._id || student?.studentid || student?.studentId;

const normaliseText = (value) => String(value || "").trim().replace(/\s+/g, " ");

const normaliseStatus = (value) => normaliseText(value).toLowerCase().replace(/\s+/g, "_");

const getLoginBypassRows = (response) => {
  if (Array.isArray(response?.rows)) return response.rows;
  if (Array.isArray(response?.students)) return response.students;
  if (Array.isArray(response?.data?.rows)) return response.data.rows;
  if (Array.isArray(response?.data?.students)) return response.data.students;
  return null;
};

const getLoginBypassCount = (response) => {
  const rows = getLoginBypassRows(response);
  if (Array.isArray(rows)) return rows.length;

  const count = [
    response?.generatedCount,
    response?.availableCount,
    response?.studentCount,
    response?.linkCount,
    response?.data?.generatedCount,
    response?.data?.availableCount,
    response?.data?.studentCount,
    response?.data?.linkCount
  ].find((value) => Number.isFinite(Number(value)));

  return count === undefined ? null : Number(count);
};

const getQrStudents = (qrData) => {
  if (Array.isArray(qrData?.students)) return qrData.students;
  if (Array.isArray(qrData?.data?.students)) return qrData.data.students;
  if (Array.isArray(qrData)) return qrData;
  if (Array.isArray(qrData?.data)) return qrData.data;
  return [];
};

const getLoginBypassBlockMessage = (response) => {
  const status = normaliseStatus(response?.status || response?.data?.status);
  const message = response?.message || response?.data?.message || "";
  const reasonText = [
    response?.reason,
    response?.code,
    response?.blockedReason,
    response?.unavailableReason,
    response?.data?.reason,
    response?.data?.code,
    response?.data?.blockedReason,
    response?.data?.unavailableReason,
    message
  ].filter(Boolean).join(" ").toLowerCase();
  const explicitUnavailable =
    response?.available === false ||
    response?.ready === false ||
    response?.canGenerate === false ||
    response?.data?.available === false ||
    response?.data?.ready === false ||
    response?.data?.canGenerate === false;
  const hasNoLinks = getLoginBypassCount(response) === 0;
  const allScreenersComplete =
    status.includes("all_screeners_complete") ||
    reasonText.includes("all_screeners_complete") ||
    (reasonText.includes("all") && reasonText.includes("screener") && reasonText.includes("complete")) ||
    reasonText.includes("no incomplete screener") ||
    reasonText.includes("no pending screener");

  if (allScreenersComplete) {
    return "Login bypass is not available because all screeners have already been completed for this class.";
  }

  if (["blocked", "unavailable", "not_ready", "not_needed"].includes(status) || explicitUnavailable || hasNoLinks) {
    return message || "Login bypass links are not available for this class right now.";
  }

  return "";
};

const normaliseClass = (classItem, countryGrades) => {
  const studentCount =
    classItem?.studentNumber ??
    classItem?.studentCount ??
    classItem?.studentsCount ??
    classItem?.students?.length;
  const name = classItem?.name || classItem?.className || "Unnamed class";

  return {
    ...classItem,
    id: getClassId(classItem),
    name,
    grade: getDistinctClassGradeLabel(classItem, name, countryGrades),
    childCount: Number.isFinite(Number(studentCount))
      ? `${Number(studentCount)} ${Number(studentCount) === 1 ? "child" : "children"}`
      : "Children in class",
    year: classItem?.academicYear || classItem?.academic_year || classItem?.academicyear || "Current year"
  };
};

function TeacherProvideLoginBypassModal({
  show,
  handleClose,
  schoolid,
  countryGrades = null,
  prefill = null,
  onAssignScreener = null,
  onViewScreenerGuidance = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("form");
  const [classes, setClasses] = useState([]);
  const [className, setClassName] = useState("");
  const [selectedClass, setSelectedClass] = useState(null);
  const [studentName, setStudentName] = useState("");
  const [classChildren, setClassChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [isLoadingChildren, setIsLoadingChildren] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [isLoadingClasses, setIsLoadingClasses] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [blockedMessage, setBlockedMessage] = useState("");
  const [availabilitySummary, setAvailabilitySummary] = useState(null);
  const [exportSummary, setExportSummary] = useState(null);

  useEffect(() => {
    if (!show) return;
    setStage("form");
    const prefilledClassName = prefill?.className || "";
    setClasses([]);
    setClassName(prefilledClassName);
    setSelectedClass(null);
    setStudentName(prefill?.studentName || "");
    setClassChildren([]);
    setSelectedChild(null);
    setIsLoadingChildren(false);
    setReason(prefill?.reason || "");
    setNote(prefill?.note || "");
    setIsSubmitting(false);
    setError("");
    setBlockedMessage("");
    setAvailabilitySummary(null);
    setExportSummary(null);

    if (!schoolid) {
      setError("Classes could not be loaded. Please try again.");
      return;
    }

    setIsLoadingClasses(true);
    getClassesBySchoolId(schoolid)
      .then((classesResponse) => {
        const normalisedClasses = (Array.isArray(classesResponse) ? classesResponse : [])
          .map((classItem) => normaliseClass(classItem, countryGrades))
          .filter((classItem) => classItem.id && classItem.active !== false)
          .sort((firstClass, secondClass) => firstClass.name.localeCompare(secondClass.name));
        const prefilledClass = normalisedClasses.find(
          (classItem) => normaliseText(classItem.name).toLowerCase() === normaliseText(prefilledClassName).toLowerCase()
        );

        setClasses(normalisedClasses);
        setSelectedClass(prefilledClass || null);
      })
      .catch((loadError) => {
        console.error("Error loading login-bypass classes:", loadError);
        setError("Classes could not be loaded. Please try again.");
      })
      .finally(() => {
        setIsLoadingClasses(false);
      });
  }, [show, schoolid, countryGrades, prefill]);

  const filteredClasses = classes.filter((classItem) =>
    normaliseText(classItem.name).toLowerCase().includes(normaliseText(className).toLowerCase())
  );
  const isIndividualExport = reason === INDIVIDUAL_REASON;
  const canContinue = Boolean(selectedClass && reason.trim().length > 0 && (!isIndividualExport || selectedChild));
  const availableLinkCount = getLoginBypassCount(availabilitySummary);
  const selectedClassSummary = selectedClass || {
    name: className,
    childCount: studentName ? "1 child" : "24 children",
    year: "2025/26"
  };
  const childrenIncluded = selectedChild
    ? selectedChild.name
    : availableLinkCount !== null
      ? `${availableLinkCount} ${availableLinkCount === 1 ? "child" : "children"}`
      : selectedClassSummary.childCount;

  const selectClass = (classResult) => {
    setSelectedClass(classResult);
    setClassName(classResult.name);
    setError("");
    setBlockedMessage("");
    setAvailabilitySummary(null);
    setClassChildren([]);
    setSelectedChild(null);
    setStudentName("");
  };

  const requestReason = [reason, note, selectedChild?.name].filter(Boolean).join(" | ");

  useEffect(() => {
    if (!show || !selectedClass || !isIndividualExport) return;
    let cancelled = false;
    setIsLoadingChildren(true);
    getQR(selectedClass.id)
      .then((response) => {
        if (cancelled) return;
        setClassChildren(getQrStudents(response).map((student) => ({
          ...student,
          id: getStudentId(student),
          name: student.name || `${student.firstName || ""} ${student.lastName || ""}`.trim() || student.username || "Unnamed child"
        })));
      })
      .catch(() => {
        if (!cancelled) setError("Children could not be loaded for this class. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setIsLoadingChildren(false);
      });
    return () => { cancelled = true; };
  }, [show, selectedClass, isIndividualExport]);

  const matchingChildren = classChildren
    .filter((child) => !studentName.trim() || normaliseText(child.name).toLowerCase().includes(normaliseText(studentName).toLowerCase()))
    .slice(0, 10);

  const buildQrExportRows = (studentsData) => studentsData.map((student) => ({
    ...student,
    id: `${selectedClass.id}-${getStudentId(student)}`,
    originalId: getStudentId(student),
    name: student.name || `${student.firstName || ""} ${student.lastName || ""}`.trim() || student.username,
    username: student.username || student.login || getStudentId(student),
    password: student.password || "********",
    class: selectedClassSummary.name,
    className: selectedClassSummary.name,
    classId: selectedClass.id,
    isSnapshot: selectedClass?.snapshot || false
  }));

  const handlePrepareSheet = async () => {
    if (!canContinue || isSubmitting) return;

    setIsSubmitting(true);
    setError("");
    setBlockedMessage("");

    try {
      const response = await getLoginBypass(selectedClass.id, requestReason);
      const nextBlockedMessage = getLoginBypassBlockMessage(response);

      if (nextBlockedMessage) {
        setBlockedMessage(nextBlockedMessage);
        return;
      }

      setAvailabilitySummary(response);
      setStage("sheet");
    } catch (availabilityError) {
      console.error("Error checking login bypass availability:", availabilityError);
      setError("Login bypass availability could not be checked. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadExport = async () => {
    if (!canContinue || isSubmitting) return;

    setIsSubmitting(true);
    setError("");

    try {
      const qrData = await getQR(selectedClass.id);
      const allRows = buildQrExportRows(getQrStudents(qrData));
      const rows = isIndividualExport
        ? allRows.filter((row) => String(row.originalId) === String(selectedChild?.id))
        : allRows;

      if (isIndividualExport && rows.length !== 1) {
        throw new Error("The selected child could not be matched in the export.");
      }

      const date = new Date().toISOString().split("T")[0];
      const safeClassName = selectedClassSummary.name.replace(/[^a-zA-Z0-9]/g, "_");
      exportLoginBypassRowsToExcel(rows, `Student_Bypass_Links_${safeClassName}_${date}`, selectedClassSummary.name);
      setExportSummary({ generatedCount: rows.length });
      setStage("success");
    } catch (exportError) {
      console.error("Error exporting login bypass sheet:", exportError);
      setError("The login bypass sheet could not be generated. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: exportSummary?.auditReference || selectedClass?.id || selectedClassSummary.name || "",
        message: "Login bypass workflow complete."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderClassResults = () => {
    if (isLoadingClasses) {
      return (
        <div className="login-bypass-search-state">
          <Spinner animation="border" size="sm" />
          <span>Loading classes...</span>
        </div>
      );
    }

    if (selectedClass) {
      return (
        <button
          aria-pressed="true"
          className="login-bypass-row selected"
          onClick={() => {
            setSelectedClass(null);
            setBlockedMessage("");
            setAvailabilitySummary(null);
          }}
          type="button"
        >
          <span className="login-bypass-radio" aria-hidden="true" />
          <span>
            <strong>{selectedClass.name}</strong>
            <span>{[selectedClass.grade, selectedClass.childCount, selectedClass.year].filter(Boolean).join(" - ")}</span>
          </span>
        </button>
      );
    }

    if (!className.trim()) {
      return <p className="login-bypass-muted">Start typing a class name.</p>;
    }

    if (filteredClasses.length < 1) {
      return <p className="login-bypass-muted">No matching classes found.</p>;
    }

    return filteredClasses.slice(0, 5).map((classResult) => (
      <button
        aria-pressed="false"
        className="login-bypass-row"
        key={classResult.id}
        onClick={() => selectClass(classResult)}
        type="button"
      >
        <span className="login-bypass-radio" aria-hidden="true" />
        <span>
          <strong>{classResult.name}</strong>
          <span>{[classResult.grade, classResult.childCount, classResult.year].filter(Boolean).join(" - ")}</span>
        </span>
      </button>
    ));
  };

  const renderForm = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Download Child Login Links</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="login-bypass-intro">Start with the class. You can search for an individual child instead if needed.</p>
        {error && <Alert variant="danger">{error}</Alert>}
        <h3 className="login-bypass-question">Which class needs bypass links?</h3>
        <Form.Group className="mb-3" controlId="provideLoginBypassClass">
          <Form.Label>Search class</Form.Label>
          <Form.Control
            value={className}
            onChange={(event) => {
              setClassName(event.target.value);
              setSelectedClass(null);
              setBlockedMessage("");
              setAvailabilitySummary(null);
            }}
          />
        </Form.Group>
        <div className="login-bypass-results" aria-label="Class results">
          {renderClassResults()}
        </div>
        {!isIndividualExport && (
          <Form.Group className="visually-hidden" controlId="provideLoginBypassChild">
            <Form.Label>Individual child</Form.Label>
            <Form.Control value={studentName} readOnly />
          </Form.Group>
        )}
        <Form.Group className="visually-hidden" controlId="provideLoginBypassNote">
          <Form.Label>Note</Form.Label>
          <Form.Control as="textarea" rows={3} value={note} onChange={(event) => setNote(event.target.value)} />
        </Form.Group>
        <Form.Group className="mt-4">
          <Form.Label className="login-bypass-question">What is happening?</Form.Label>
          <div className="login-bypass-options">
            {reasons.map((option) => (
              <label className={`login-bypass-option${reason === option.label ? " selected" : ""}`} key={option.label}>
                <Form.Check
                  type="radio"
                  id={`provide-login-${option.label}`}
                  name="provide-login-reason"
                  checked={reason === option.label}
                  onChange={() => {
                    setReason(option.label);
                    setSelectedChild(null);
                    setStudentName("");
                    setBlockedMessage("");
                    setAvailabilitySummary(null);
                  }}
                />
                <span>
                  <strong>{option.label}</strong>
                  <span>{option.helper}</span>
                </span>
              </label>
            ))}
          </div>
        </Form.Group>
        {isIndividualExport && selectedClass && (
          <Form.Group className="mt-4" controlId="provideLoginBypassChild">
            <Form.Label>Which child needs a login link?</Form.Label>
            <Form.Control
              type="search"
              value={studentName}
              onChange={(event) => {
                setStudentName(event.target.value);
                setSelectedChild(null);
              }}
              placeholder="Search this class"
              aria-label="Search child in selected class"
            />
            <div className="login-bypass-results" aria-label="Matching children">
              {isLoadingChildren ? (
                <div className="login-bypass-search-state"><Spinner animation="border" size="sm" /><span>Loading children...</span></div>
              ) : matchingChildren.map((child) => (
                <button
                  type="button"
                  key={child.id}
                  className={`login-bypass-row${selectedChild?.id === child.id ? " selected" : ""}`}
                  onClick={() => {
                    setSelectedChild(child);
                    setStudentName(child.name);
                  }}
                  aria-pressed={selectedChild?.id === child.id}
                >
                  <span className="login-bypass-radio" aria-hidden="true" />
                  <span><strong>{child.name}</strong><span>{selectedClass.name}</span></span>
                </button>
              ))}
            </div>
          </Form.Group>
        )}
        {blockedMessage && <Alert variant="warning">{blockedMessage}</Alert>}
      </Modal.Body>
      <Modal.Footer>
        <span className="login-bypass-footer-note">{canContinue ? `${selectedClassSummary.name} selected.` : isIndividualExport ? "Choose one child to continue." : "Choose a class and reason first."}</span>
        <Button variant="outline-secondary" onClick={handleClose}>Cancel</Button>
        <Button variant="primary" onClick={handlePrepareSheet} disabled={!canContinue || isSubmitting}>
          {isSubmitting ? "Checking..." : "Next"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSheet = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Download Child Login Links</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="login-bypass-intro">Use these links when children cannot scan QR codes.</p>
        {error && <Alert variant="danger">{error}</Alert>}
        <Alert variant="warning" className="login-bypass-warning">
          <strong>Ready to export</strong>
          <div>The sheet is generated only after the export is confirmed.</div>
        </Alert>
        <h3 className="login-bypass-question">Sheet summary</h3>
        <div className="login-bypass-summary">
          <div><strong>Class</strong><span>{selectedClassSummary.name}</span></div>
          <div><strong>Children included</strong><span>{childrenIncluded}</span></div>
          <div><strong>Reason</strong><span>{reason}</span></div>
          <div><strong>Sheet contents</strong><span>One assessment login link for each child</span></div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span className="login-bypass-footer-note">Child login links ready to download.</span>
        <Button variant="outline-secondary" onClick={() => setStage("form")}>Back</Button>
        <Button variant="primary" onClick={handleDownloadExport} disabled={isSubmitting}>
          {isSubmitting ? "Generating..." : "Download Excel sheet"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccess = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Child Login Links Ready</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="login-bypass-intro">The Excel sheet has been downloaded.</p>
        <Alert variant="success" className="login-bypass-success">
          <span className="login-bypass-ok-badge">OK</span>
          <span>
            <strong>The child login link sheet is ready for {selectedClassSummary.name}.</strong>
            <span>
              {exportSummary?.generatedCount ?? exportSummary?.rows?.length ?? 0} rows generated. {exportSummary?.auditReference ? `Audit reference: ${exportSummary.auditReference}.` : ""}
            </span>
          </span>
        </Alert>
        <div className="login-bypass-next">
          <strong>What next?</strong>
          <div className="text-muted">
            {embedded
              ? "Done returns to SENSAI. Create another sheet repeats the action."
              : "Done returns to Help Centre home. Create another sheet repeats the action."}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="login-bypass-success-footer">
        <span className="login-bypass-footer-note">Task complete.</span>
        <Button variant="outline-secondary" onClick={() => setStage("form")}>Create another sheet</Button>
        {onAssignScreener && (
          <Button
            variant="outline-secondary"
            onClick={() => onAssignScreener({ className: selectedClassSummary.name })}
          >
            Assign screener
          </Button>
        )}
        {onViewScreenerGuidance && (
          <Button
            variant="outline-secondary"
            onClick={() => onViewScreenerGuidance({
              guidanceQuery: "Before-screening video"
            })}
          >
            View screener guidance
          </Button>
        )}
        <Button variant="primary" onClick={handleDone}>Done</Button>
      </Modal.Footer>
    </>
  );

  return (
    <Modal show={show} onHide={handleModalHide} centered backdrop="static" className="teacher-provide-login-bypass-modal">
      {stage === "form" && renderForm()}
      {stage === "sheet" && renderSheet()}
      {stage === "success" && renderSuccess()}
    </Modal>
  );
}

export default TeacherProvideLoginBypassModal;
